import {
  EnumOptionsType,
  RJSFSchema,
  UiSchema,
  WidgetProps,
} from '@rjsf/utils';
import React, { useEffect } from 'react';
import { KatDropdown } from '@amzn/katal-react';
import { MANDATORY_FIELD_IDENTIFIER } from 'src/react-jsonschema-katal/katal/constants/ApplicationConstants';
import styled from 'styled-components';
import { TextViewWidget } from 'src/react-jsonschema-katal/katal/components/ViewWidget';
import { useLocalizationContext } from '@amzn/react-arb-tools';
/**
 * Check if the dropdown is single select or multiselect.
 * @param uiSchema uiSchema
 * @returns boolean
 */
const isMultipleSelection = (
  uiSchema: UiSchema<any, RJSFSchema, any> | undefined,
) => {
  return uiSchema?.['ui:multiple'] || false;
};

/**
 * Get the default value of the dropdown.
 * @param uiSchema uiSchema
 * @returns string if the value is present else undefined.
 */
const getDefaultValue = (
  uiSchema: UiSchema<any, RJSFSchema, any> | undefined,
): string => {
  return uiSchema?.['ui:defaultValue'];
};

const DropdownWidgetArea = styled.div`
  kat-dropdown {
    --font-size: ${({ theme }) => theme.fontSize.m};
  }
`;

/**
 * Method to find the Dropdown label based on values.
 * The dropdown selected values might be a list or a string.
 *
 * @param enumOptions RJSF schema for the enum
 * @param values the selected values.
 * @returns the labels of the selected values.
 */
export function findDropDownLabel(
  enumOptions: EnumOptionsType<RJSFSchema>[] | undefined,
  values: any,
) {
  if (!enumOptions || !values) {
    return null;
  }
  const originalValues = values;
  const dropdownOptions = {};
  enumOptions.forEach((enumOption: EnumOptionsType<RJSFSchema>) => {
    dropdownOptions[enumOption.value] = enumOption.label;
  });

  if (values instanceof Array) {
    values = values.map((value) => dropdownOptions[value]);
  } else {
    values = dropdownOptions[values.toString()];
  }
  return !values ? originalValues : values;
}

const DropdownWidget: React.FC<WidgetProps> = ({
  label,
  value,
  onChange,
  disabled,
  required,
  uiSchema,
  options,
  rawErrors,
}) => {
  const { localizationContext } = useLocalizationContext();
  if (disabled) {
    const dropdownLabel = findDropDownLabel(options.enumOptions, value);
    return <TextViewWidget label={label} value={dropdownLabel} />;
  }

  if (required) {
    label = `${label}${MANDATORY_FIELD_IDENTIFIER}`;
  }
  const additionalErrorProps: KatDropdown._OwnProps = {};
  if (rawErrors && rawErrors.length > 0) {
    additionalErrorProps.state = 'error';
    additionalErrorProps.stateLabel = rawErrors
      .filter((error, index) => rawErrors.indexOf(error) === index)
      .join(',');
  }

  //Filtering out "" options which are used just for default: '' to support empty values.
  const filteredEnumOptions = options.enumOptions?.filter(
    (enumOptions: EnumOptionsType<RJSFSchema>) => enumOptions.label != '',
  );
  const optionList = filteredEnumOptions?.map(
    (enumOptions: EnumOptionsType<RJSFSchema>) => {
      return { name: enumOptions.label, value: enumOptions.value };
    },
  );

  useEffect(() => {
    const initialvalue = getDefaultValue(uiSchema);
    if (!value && initialvalue !== undefined) {
      onChange(initialvalue);
    }
  }, []);

  if (isMultipleSelection(uiSchema)) {
    return (
      <DropdownWidgetArea>
        <KatDropdown
          locale={localizationContext.getLocale()}
          label={label}
          onChange={(e: KatDropdown.ChangeEvent) => onChange(e.detail.values)}
          options={optionList}
          values={value}
          multiple
          disabled={disabled}
          {...additionalErrorProps}
        ></KatDropdown>
      </DropdownWidgetArea>
    );
  }
  return (
    <DropdownWidgetArea>
      <KatDropdown
        label={label}
        onChange={(e: KatDropdown.ChangeEvent) => onChange(e.detail.value)}
        options={optionList}
        value={value}
        disabled={disabled}
        {...additionalErrorProps}
      ></KatDropdown>
    </DropdownWidgetArea>
  );
};

export default DropdownWidget;
